import { useEffect, useState } from 'react';
import { api, type BankTxFinInfo } from '../lib/api';
import { ErrorBox, dash, money, MoneyInput } from '../components/DataTable';
import { AllocPicker } from './FinInst';
import { DeductAccountPicker } from './CashBook';
import { canScreen, useMe } from '../lib/me';

/**
 * 게시판 #156 — 입금 내역에서 구분이 「재무추가」인 입금을 기사 재무추가 **지급**으로 넣는다(출납일보 공제의 거울).
 * 편성(누구에게) · 지급 계정 · 정산적용월(기본 = 거래일 6일~다음 달 5일) · 금액(기본 = 입금액, 그 이하만).
 * 넣은 뒤엔 같은 창에서 「되돌리기」(재무추가 줄을 지운다 · 마감 달은 막힌다). 서버 `functions/src/bankFin.ts`.
 */
export function BankFinForm({ bankTxId, onClose, onChanged }: { bankTxId: number; onClose: () => void; onChanged: () => void }) {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [info, setInfo] = useState<BankTxFinInfo | null>(null);
  const [allocId, setAllocId] = useState<number | null>(null);
  const [account, setAccount] = useState('');
  const [mon, setMon] = useState('');
  // 정산월 없음 = 이월잔액(drive_mon '' · #156 후속 정여진). 켜면 정산월 칸은 무시한다.
  const [noMon, setNoMon] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [undoArm, setUndoArm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getBankTxFin({ id: bankTxId }).then((r) => {
      if (cancelled) return;
      setInfo(r);
      setMon(r.defaultMon ?? '');
      setAmount(r.tx ? String(Math.round(Number(r.tx.amount_in))) : '');
    }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [bankTxId]);

  const finished = !noMon && (info?.finishedMons.includes(mon) ?? false);
  const over = info?.tx ? Number(amount || 0) > Number(info.tx.amount_in) : false;
  const ready = !!info && info.open && !info.blockedReason && !info.link && canWrite
    && !!allocId && !!account && (noMon || /^[0-9]{4}-[0-9]{2}$/.test(mon)) && !finished && Number(amount) > 0 && !over;

  async function commit() {
    if (!ready) return;
    setBusy(true); setError(null);
    try {
      await api.commitBankTxFin({ id: bankTxId, carAllocId: allocId!, accountName: account, driveMon: noMon ? '' : mon, amount });
      onChanged(); onClose();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  async function undo() {
    if (!undoArm) { setUndoArm(true); return; }
    setUndoArm(false); setBusy(true); setError(null);
    try { await api.undoBankTxFin({ id: bankTxId }); onChanged(); onClose(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }

  const link = info?.link ?? null;
  return (
    <div className="card space-y-3 p-5 text-sm">
      <h2 className="text-lg font-semibold">재무추가 지급으로 넣기</h2>
      {!info && !error && <p className="text-ink-soft">불러오는 중…</p>}
      {info?.tx && (
        <p className="rounded-lg bg-canvas px-3 py-2">
          {info.tx.tx_date} · 내용 <b>{dash(info.tx.memo)}</b> · 입금 <b>{money(info.tx.amount_in)}</b>
          {info.comment && <> · 비고로 들어갈 글 <b>{info.comment}</b></>}
        </p>
      )}
      {link ? (
        <div className="space-y-2">
          <p className="text-emerald-700">
            넣음 — 정산월 <b>{link.drive_mon || '없음(이월잔액)'}</b> · {money(link.amount)} · 재무추가 NO <b>{link.result_daily_id}</b>
            {link.account_name && <> · {link.account_name}</>}
            {(link.car_no || link.driver_name) && <> · {dash(link.car_no)} {dash(link.driver_name)}</>}
          </p>
          <p className="text-xs text-ink-faint">{dash(link.created_by)} · {link.created_at}</p>
          {(link.rd_deleted !== false || link.rd_amount === null || Number(link.rd_amount) !== Number(link.amount)) && (
            <p className="text-amber-700">재무추가 화면에서 이 줄이 지워졌거나 금액이 바뀌었습니다. 되돌린 뒤 다시 넣으세요.</p>
          )}
          <p className="text-xs text-ink-soft">구분을 다른 값으로 바꾸거나 금액·편성을 고치려면 먼저 되돌리세요.</p>
        </div>
      ) : info && (
        <div className="space-y-3">
          {!info.open && <p className="rounded bg-amber-50 px-3 py-2 text-amber-800">재무추가 바로 넣기가 닫혀 있습니다(출납일보 공제와 같은 스위치 — 관리자가 엽니다).</p>}
          {info.blockedReason && <p className="rounded bg-amber-50 px-3 py-2 text-amber-800">{info.blockedReason}</p>}
          {!info.blockedReason && (
            <>
              <AllocPicker value={allocId} onPick={setAllocId} initialTerm={info.plate} />
              {info.plate && <p className="pl-[5.5rem] text-xs text-ink-faint">적요의 차량번호 「{info.plate}」로 찾은 편성입니다 — 맞는 편성을 고르세요.</p>}
              <DeductAccountPicker value={account} options={info.accounts} onPick={setAccount} />
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-20">정산적용월</span>
                <input type="month" className="field w-40" value={mon} disabled={noMon} onChange={(e) => setMon(e.target.value)} />
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={noMon} onChange={(e) => setNoMon(e.target.checked)} />
                  정산월 없음(이월잔액)
                </label>
                {finished && <span className="text-red-600">마감된 달입니다 — 다른 달을 고르세요.</span>}
                {!noMon && !finished && info.defaultMon && mon !== info.defaultMon && <span className="text-xs text-ink-faint">기본 {info.defaultMon}(거래일 6일~다음 달 5일)</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-20">금액</span>
                <MoneyInput className="w-40" value={amount} onChange={setAmount} />
                {over && <span className="text-red-600">입금액보다 클 수 없습니다.</span>}
              </div>
            </>
          )}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <div className="flex justify-end gap-2">
        {link && canWrite && (
          <button type="button" className="btn-ghost mr-auto text-red-600" disabled={busy} onClick={undo}>
            {undoArm ? '한 번 더 누르면 재무추가 줄을 지웁니다' : '되돌리기'}
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={onClose}>닫기</button>
        {!link && <button type="button" className="btn-primary" disabled={busy || !ready} onClick={commit}>재무추가에 넣기</button>}
      </div>
    </div>
  );
}
